/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
  IActionContext,
  IComponentProps,
  IGridSettings,
  IImageSettings,
  Image,
} from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
  CartLine,
  SalesLine
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import React, { useEffect, useState } from 'react';
import { brandCodeParser } from '../Utilities/brand-code-parser';
import ProductType from '../Utilities/productType';
import { MasterProdDisplay } from './master-prod-display-name';

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs
 */
export interface ICartlineResourceString {
  /** Display string for discount label */
  discountStringText: string;
  /** String for size key  */
  sizeString: string;
  /** String for color key  */
  colorString: string;
  /** String associated with the configuration product dimension  */
  configString: string;
  /** String for style key  */
  styleString: string;
  /** Display string for quantity label  */
  quantityDisplayString: string;
  /** Display string for quantity label  */
  inputQuantityAriaLabel: string;
}

interface ICartLineData {
  cartLine: CartLine | SalesLine;
  product: SimpleProduct;
}

/**
 * ICartLineProps: The props required to render cartLineitem
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
  /** The flag to change the quantity component from interactible to static  */
  disableQuantityInteractivity?: boolean;
  /** The primary image url  */
  primaryImageUrl?: string;
  /** The product url  */
  productUrl?: string;
  /** GridSettings for the product image in cartLine  */
  gridSettings: IGridSettings;
  /** ImageSettings for the product image in cartLine  */
  imageSettings: IImageSettings;
  /** Boolean flag to indicate if the item is out of stock  */
  isOutOfStock?: boolean;
  /** Flag to make quantity section editable  */
  isQuantityEditable?: boolean;
  /** Max quantity for line item  */
  maxQuantity?: number;
  /** Current quantity for line item  */
  currentQuantity?: number;
  /** Resource string for the component  */
  resources: ICartlineResourceString;
  /** SalesLine flag  */
  isSalesLine?: boolean;
  //   Remove button coming from smwe-cartline
  // remove:string;
  remove?: React.ReactNode;
  /** Display images for on cart lines  */
  showImages?: boolean;
  /** Has zero availability error?  */
  zeroAvailabilityError?: boolean;
  cartState?: ICartState;
  cartLineErrors?: { [index: string]: boolean };
  /** Replaces discount code name with percent  */
  showDiscountPercent?: boolean;
  /** Quantity onChange callback  */
  quantityOnChange?(cartLine: CartLine, newQuantity: number): void;
  cartLineOnChange?(ctx: IActionContext, cartState: ICartState | undefined, cartLine: CartLine): void;
}

// tslint:disable-next-line:max-func-body-length
const SmweCartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
  const { isSalesLine, productUrl, resources, primaryImageUrl, showImages, gridSettings, imageSettings, zeroAvailabilityError } = props;
  const intlFormatter = props.context.cultureFormatter;
  const { product, cartLine } = props.data;
  const { inputQuantityAriaLabel } = props.resources;
  const _updateQuantity = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (props.quantityOnChange) {
      props.quantityOnChange(
        props.data.cartLine,
        parseInt(event.target.value, 10)
      );
    }
  };

  const _generateMenu = (quantity: number) => {
    const nodes = [];

    for (let i = 1; i <= quantity; i++) {
      // tslint:disable-next-line:react-a11y-role-has-required-aria-props
      nodes.push(
        <option
          className='msc-cart-line__quantity__select-menu__item'
          value={i}
          aria-selected={true}
        >
          {i}
        </option>
      );
    }

    return nodes;
  };

  const _generateSelectMenu = (
    quantity: number,
    currentQuantity: number | undefined
  ): JSX.Element => {
    const shouldDisableQty = props.data.product && props.data.product.ItemId?.toLowerCase() === props.context.request.channel?.GiftCardItemId?.toLowerCase();
    return (
      <td className='header-qty'>
        <select
          className='msc-cart-line__quantity__select-menu'
          aria-label={inputQuantityAriaLabel}
          value={currentQuantity}
          onChange={_updateQuantity}
          disabled={zeroAvailabilityError || shouldDisableQty}
        >
          {_generateMenu(quantity)}
        </select>
        {props.remove}
      </td>
    );
  };

  const _generateQuantityAndPrice = (): JSX.Element[] | null => {
    const nodes = [];
    const priceAdjustmentSavings = _getPriceAdjustmentSavings();
    const productType = new ProductType();
    productType.check(cartLine.DeliveryMode, props.context);

    const isWineProduct = productType.isWineProduct();
    if (isWineProduct && props.isOutOfStock && !zeroAvailabilityError) {
      return null;
    }

    if (props.isQuantityEditable) {
      nodes.push(
        _generateSelectMenu(props.maxQuantity || 10, props.currentQuantity)
      );
    } else {
      nodes.push(
        <div className='msc-cart-line__quantity'>
          <label className='quantity-label'>
            {resources.quantityDisplayString}
          </label>
          <span className='quantity-value'>
            {props.data.cartLine.Quantity} {props.remove}
          </span>
        </div>
      );
    }
    nodes.push(
      <td className='msc-cart-line__product-price'>
          <div className='price price-actual' tabIndex={0}>
            {props.data.cartLine.Price !== undefined
              ? intlFormatter.formatCurrency(props.data.cartLine.Price)
              : ''}
            {_getPriceAdjustmentPercent()}
          </div>
          {_getPriceDiscount()}
      </td>
    );
    nodes.push(
      <td className='cart-line-item-price'>
        <div className='msc-price discount-value msc-cart-price' tabIndex={0}>
            <React.Fragment>
              <div>
                {isSalesLine
                  ? props.data.cartLine.Price &&
                  intlFormatter.formatCurrency(props.data.cartLine.Price)
                  : props.data.cartLine.NetPrice === 0 ? intlFormatter.formatCurrency(0)
                    : props.data.cartLine.NetPrice &&
                    intlFormatter.formatCurrency(props.data.cartLine.NetPrice)}
              </div>
              {(props.data.cartLine.DiscountAmountWithoutTax &&
                props.data.cartLine.DiscountAmountWithoutTax > 0) ||
                (priceAdjustmentSavings &&
                  priceAdjustmentSavings > 0)
                ? `Savings ${intlFormatter.formatCurrency(
                  (props.data.cartLine.DiscountAmountWithoutTax || 0) + _getPriceAdjustmentSavings()
                )}`
                : null}
            </React.Fragment>
          </div>
      </td>
    );
    return nodes;
  };

  const _getCartLineImage = (): JSX.Element | null => {
    if (showImages && primaryImageUrl) {
      return (
        <div className='cart-line-item-product-image'>
          <Image
            src={primaryImageUrl}
            altText=''
            gridSettings={gridSettings}
            imageSettings={imageSettings}
          />
        </div>
      );
    } else {
      return null;
    }
  };

  const _getPriceAdjustmentPercent = (): JSX.Element | null => {
    if (props.data.product.Price && props.data.cartLine.Price && (props.data.cartLine.Price < props.data.product.Price)) {
      return <span className='price-adjusted-percent'>{` (${(100 - ((props.data.cartLine.Price / props.data.product.Price) * 100)).toFixed(0)}% Off)`}</span>;
    } else {
      return null;
    }
  };

  const _getPriceDiscount = (): string | null => {
    if (
      props.data.cartLine.DiscountAmountWithoutTax !== undefined &&
      props.data.cartLine.DiscountAmountWithoutTax !== 0 &&
      props.data.cartLine.Quantity &&
      props.data.cartLine.DiscountLines
    ) {
      if (props.showDiscountPercent && props.data.cartLine.NetPrice) {
        return `${((props.data.cartLine.DiscountAmountWithoutTax / props.data.cartLine.NetPrice) * 100).toFixed(0)}% off`;
      }
      return `${_doubleDiscounts()} ${props.data.cartLine.DiscountLines[0].DiscountCode}`;

    }
    return null;
  };

  enum GiftCardFieldType {
    Extension, Attribute
  }

  interface IGiftCardFields {
    Key: string;
    Type: GiftCardFieldType;
    StringValue: string;
    Required: boolean;
    isValid?: boolean;
  }

  interface IGiftCardFormObj {
    FirstName: IGiftCardFields;
    LastName: IGiftCardFields;
    ElectronicDeliveryEmail: IGiftCardFields;
    ElectronicDeliveryEmailContent: IGiftCardFields;
  }

  const giftCardFormObj: IGiftCardFormObj = {
    FirstName: {
      Key: 'FirstName',
      Type: GiftCardFieldType.Extension,
      StringValue: '',
      Required: true,
      isValid: true
    },
    LastName: {
      Key: 'LastName',
      Type: GiftCardFieldType.Extension,
      StringValue: '',
      Required: true,
      isValid: true
    },
    ElectronicDeliveryEmail: {
      Key: 'ElectronicDeliveryEmail',
      Type: GiftCardFieldType.Attribute,
      StringValue: '',
      Required: true,
      isValid: true
    },
    ElectronicDeliveryEmailContent: {
      Key: 'ElectronicDeliveryEmailContent',
      Type: GiftCardFieldType.Attribute,
      StringValue: '',
      Required: false
    },
  };

  // tslint:disable-next-line: max-func-body-length
  const _renderGiftCardPrompt = (): JSX.Element | null => {

    if (!props.data.cartLine.IsGiftCardLine) {
      return null;
    }

    const [giftCardFormField, setGiftCardFormField] = useState(giftCardFormObj);
    const [formFieldsDebounce, setFormFieldsDebounce] = useState('');

    const setFormFieldState = () => {
      const unhydratedCartLineLoad = { ...props.data.cartLine };
      Object.keys(giftCardFormField).forEach((field) => {
        const fieldState = giftCardFormField[field];
        if (fieldState.Type === GiftCardFieldType.Extension) {
          unhydratedCartLineLoad.ExtensionProperties = unhydratedCartLineLoad.ExtensionProperties || [];
          const property = unhydratedCartLineLoad.ExtensionProperties.find((extension) => {
            return extension.Key?.toLowerCase() === fieldState.Key?.toLowerCase();
          });
          if (property) {
            setGiftCardFormField((prevState: IGiftCardFormObj) => ({
              ...prevState,
              [field]: {
                ...prevState[field],
                StringValue: property.Value?.StringValue
              }
            }));
          }
        } else if (fieldState.Type === GiftCardFieldType.Attribute) {
          const property = unhydratedCartLineLoad[fieldState.Key];
          if (property) {
            setGiftCardFormField((prevState: IGiftCardFormObj) => ({
              ...prevState,
              [fieldState.Key]: {
                ...prevState[fieldState.Key],
                StringValue: unhydratedCartLineLoad[fieldState.Key]
              }
            }));
          }
        }
      });
    };

    const updateFormFieldToCartLine = () => {
      const unhydratedCartLineLoad = { ...props.data.cartLine };
      Object.keys(giftCardFormField).forEach((field) => {
        const fieldState = giftCardFormField[field];
        if (fieldState.Type === GiftCardFieldType.Extension) {
          unhydratedCartLineLoad.ExtensionProperties = unhydratedCartLineLoad.ExtensionProperties || [];
          unhydratedCartLineLoad.ExtensionProperties.push({
            Key: fieldState.Key,
            Value: {
              StringValue: fieldState.StringValue
            }
          });
        } else if (fieldState.Type === GiftCardFieldType.Attribute) {
          unhydratedCartLineLoad[fieldState.Key] = giftCardFormField[fieldState.Key].StringValue;
        }
      });
      // Removing 'ItemTaxGroupId' from the object because now in new SSK its READONLY Attribute, can't be updated.
      delete (unhydratedCartLineLoad.ItemTaxGroupId);
      props.cartLineOnChange && props.cartLineOnChange(props.context.actionContext, props.cartState, unhydratedCartLineLoad);
    };

    const giftCardValidation = () => {
      let giftCardFormIsValid = true;
      Object.keys(giftCardFormField).forEach((field) => {
        const fieldState = giftCardFormField[field];
        if (fieldState.Required) {
          if (fieldState.StringValue.trim() === '') {
            giftCardFormIsValid = false;
            fieldState.isValid = false;
          } else {
            fieldState.isValid = true;
          }

          if (fieldState.Key === 'ElectronicDeliveryEmail') {
            const validEmailRegex =
              RegExp(/^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i);
            if (!validEmailRegex.test(fieldState.StringValue)) {
              giftCardFormIsValid = false;
              fieldState.isValid = false;
            } else {
              fieldState.isValid = true;
            }
          }
        }
      });
      if (props.cartLineErrors) {
        if (giftCardFormIsValid) {
          props.cartLineErrors[props.data.cartLine.LineId!] = false;
        } else {
          props.cartLineErrors[props.data.cartLine.LineId!] = true;
        }
      }
    };

    // Setting the data from the cartline to State on load
    useEffect(() => {
      setFormFieldState();
    }, []);

    //Checking giftCard form validation
    useEffect(() => {
      giftCardValidation();
    }, [giftCardFormField]);

    // Updating formfield to cardLine
    useEffect(() => {
      const delayDebounceFn = setTimeout(() => {
        updateFormFieldToCartLine();
      }, 600);

      return () => clearTimeout(delayDebounceFn);
    }, [formFieldsDebounce]);

    const handleChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event?.target.value;
      if (name) {
        setGiftCardFormField((prevState: IGiftCardFormObj) => ({
          ...prevState,
          [name]: {
            ...prevState[name],
            StringValue: value,
          },
        }));
        setFormFieldsDebounce(value);
      }
    };

    return (
      <div className='form-row gift-cart-form'>
        <div className='col-12 col-md-7 col-lg-6'>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label>Recipient’s First Name*</label>
              <input type='text' className={`form-control  ${!giftCardFormField.FirstName.isValid && 'is-invalid'}`} value={giftCardFormField.FirstName.StringValue} onChange={handleChange('FirstName')} />
            </div>
            <div className='form-group col-md-6'>
              <label>Last Name*</label>
              <input type='text' className={`form-control  ${!giftCardFormField.LastName.isValid && 'is-invalid'}`} value={giftCardFormField.LastName.StringValue} onChange={handleChange('LastName')} />
            </div>
          </div>
          <div className='form-row'>
            <div className='form-group col-12'>
              <p>IMPORTANT: Gift Cards are sent to the recipient email address the same day orders are placed. You can use your own e-mail address to keep it a surprise.</p>
              <label>Email Address*</label>
              <input
                type='text'
                className={`form-control  ${!giftCardFormField.ElectronicDeliveryEmail.isValid && 'is-invalid'}`}
                // @ts-ignore
                value={giftCardFormField.ElectronicDeliveryEmail.StringValue}
                onChange={handleChange('ElectronicDeliveryEmail')}
              />
            </div>
          </div>
          <div className='form-row'>
            <div className='col-12'>
              <label>Gift Message</label>
              <textarea
                className='form-control'
                value={giftCardFormField.ElectronicDeliveryEmailContent.StringValue}
                onChange={handleChange('ElectronicDeliveryEmailContent')}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const _getCartLineComment = (): JSX.Element | null => {
    const productType = new ProductType();
    productType.check(cartLine.DeliveryMode, props.context);
    if (productType.isWineProduct() && props.data.cartLine.Comment) {
      return (
        <div className='cart-line-item-error'>
          <span>{props.data.cartLine.Comment}</span>
        </div>);
    } else {
      return null;
    }
  };

  const _getPriceAdjustmentSavings = (): number => {
    if (props.data.product.Price && props.data.cartLine.Price && props.data.cartLine.NetPrice) {
      const priceUnadjusted = (props.data.cartLine.NetPrice / (props.data.cartLine.Price / props.data.product.Price));
      return (priceUnadjusted - props.data.cartLine.NetPrice);
    } else {
      return 0;
    }
  };

  const _doubleDiscounts = (): string => {
    if (_getPriceAdjustmentPercent()) {
      return '+ ';
    } else {
      return '';
    }
  };

  const brandCode = brandCodeParser({ description: product.Description, appContext: props.context });

  let jsxName = <React.Fragment>{product.Name}</React.Fragment>;
  if (product.MasterProductId) {
    jsxName = <MasterProdDisplay masterProdId={product.MasterProductId} variantName={product.Name!} props={props} />;
  }

  return (
    <div className='cart-line-item'>
      <div className='cart-line-item-content'>
        <tr className='cart-line-item-product'>
          <td className='cart-line-item-product-details'>
            <a className='msc-cart-line__product-title' href={productUrl}>
              {_getCartLineImage()}
              <div className='cart-line-item-product-heading'>
                {brandCode &&
                  <div className='cart-line-item-product-brand'>
                    {brandCode}
                  </div>
                }
                <div className='cart-line-item-product-name'>
                  {jsxName}
                </div>
              </div>
            </a>
            <div className='cart-line-item-product-dimensions' />
          </td>
          {_generateQuantityAndPrice()}
        </tr>
        {_getCartLineComment()}
        {_renderGiftCardPrompt()}
      </div>
    </div>
  );
};

// @ts-ignore
export default SmweCartLine;
